import { useState, useEffect, useRef, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import SuiTypography from "components/SuiTypography";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";

import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";

import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import StoreIcon from "@mui/icons-material/Store";
import MessageIcon from "@mui/icons-material/Message";

import toast from "react-hot-toast";

import { StoreContext } from "context/store";
import { AccountContext } from "context/account";

import Skeleton from "@mui/material/Skeleton";
import UploadProfileModal from "pages/de/portfolio/components/UploadProfileModal";
import DraggableDialog from "components/UpStyled/DraggableDialog";
import ChatRoom from "pages/messages/chatroom";

import api from "services/api";

import ReadMore from "components/UpStyled/ReadMore";

import { addProtocol } from "utils/helper";

const Profile = ({ userData, slot = "profile", handleRefresh }) => {
  const [uploadProfileVisible, setUploadProfileVisible] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(new Map());

  const { store } = useContext(StoreContext);
  const account = useContext(AccountContext);

  const params = useParams();
  const handle = params?.handle;

  let portfolioData = account?.metadata?.portfolio_content || {};
  let storeData = store;
  if (handle) {
    portfolioData = userData?.metadata?.portfolio_content || {};
    storeData = userData?.stores?.[0] || {};
  }

  useEffect(() => {
    handleRefresh();
  }, [handle]);

  const handleShowUploadProfile = () => {
    handle ? null : setUploadProfileVisible(true);
  };

  const retrieveChats = async (userId) => {
    if (!userId) {
      return;
    }
    try {
      const { data } = await api.chatroom.addChatRoom({
        userids: [userId],
      });
      setCurrentRoom(data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleOpenChat = async () => {
    if (userData?.id) {
      await retrieveChats(userData?.id);
      setChatVisible(true);
    }
  };

  return (
    <SuiBox>
      <SuiBox sx={{ position: "relative" }}>
        <SuiBox display="flex" justifyContent="center" alignItems="flex-center">
          <Tooltip title={handle ? "" : "Click to update profile"}>
            <SuiBox>
              {storeData?.metadata?.avatar ? (
                <Avatar
                  src={storeData?.metadata?.avatar}
                  sx={{
                    width: 120,
                    height: 120,
                    cursor: handle ? "cursor" : "pointer",
                  }}
                  onClick={handleShowUploadProfile}
                />
              ) : (
                <Skeleton
                  animation={false}
                  variant="circular"
                  width={120}
                  height={120}
                  sx={{ margin: "0px auto", cursor: handle ? "cursor" : "pointer" }}
                  onClick={handleShowUploadProfile}
                />
              )}
              {storeData?.name ? (
                <SuiBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <SuiTypography variant="h6">{storeData.name}</SuiTypography>
                  {account.id != userData?.id ? (
                    <MessageIcon
                      fontSize="small"
                      sx={{ cursor: "pointer" }}
                      onClick={handleOpenChat}
                    />
                  ) : null}
                </SuiBox>
              ) : (
                <Skeleton animation={false} height={60} width={400} sx={{ margin: "0px auto" }} />
              )}
            </SuiBox>
          </Tooltip>
        </SuiBox>

        <SuiBox sx={{ position: "absolute", right: 4, bottom: 0 }}>
          <SuiBox display="flex">
            {storeData?.metadata?.company_link && (
              <a
                target={"_blank"}
                href={addProtocol(storeData?.metadata?.company_link)}
                className="text-gray-800 mr-2"
              >
                <StoreIcon fontSize="small" />
              </a>
            )}
            {storeData?.metadata?.instagram_link && (
              <a
                target={"_blank"}
                href={addProtocol(storeData?.metadata?.instagram_link)}
                className="text-gray-800 mr-2"
              >
                <InstagramIcon fontSize="small" />
              </a>
            )}
            {storeData?.metadata?.facebook_link && (
              <a
                target={"_blank"}
                href={addProtocol(storeData?.metadata?.facebook_link)}
                className="text-gray-800 mr-2"
              >
                <FacebookIcon fontSize="small" />
              </a>
            )}
            {storeData?.metadata?.twitter_link && (
              <a
                target={"_blank"}
                href={addProtocol(storeData?.metadata?.twitter_link)}
                className="text-gray-800 mr-2"
              >
                <TwitterIcon fontSize="small" />
              </a>
            )}
          </SuiBox>{" "}
        </SuiBox>
      </SuiBox>

      <Tooltip title={handle ? "" : "Click to update profile"}>
        <SuiBox onClick={handleShowUploadProfile} sx={{ cursor: handle ? "cursor" : "pointer" }}>
          {storeData?.story ? (
            <SuiBox>
              <ReadMore limit={800} variant="body1" textTransform="none" color="text">
                {storeData.story}
              </ReadMore>
            </SuiBox>
          ) : (
            <SuiBox>
              <Skeleton animation={false} />
              <Skeleton animation={false} />
              <Skeleton animation={false} />
              <Skeleton animation={false} />
            </SuiBox>
          )}
        </SuiBox>
      </Tooltip>

      {uploadProfileVisible ? (
        <UploadProfileModal
          handleClose={() => setUploadProfileVisible(false)}
          visible={uploadProfileVisible}
          handleRefresh={() => {
            handleRefresh();
          }}
          userData={userData}
        />
      ) : null}
      {chatVisible ? (
        <DraggableDialog
          heading={`Chat with ${userData?.stores?.[0]?.name || userData?.name}`}
          text={
            <SuiBox>
              <ChatRoom id={currentRoom?.id} />
            </SuiBox>
          }
          visible={chatVisible}
          hideAction={true}
          handleClose={() => {
            setChatVisible(false);
          }}
        />
      ) : null}
    </SuiBox>
  );
};

export default Profile;
